import React, { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { CurrencyDisplay, CurrencyInput, KgDisplay, KgInput, NegativeKgInput } from "../../../Utils/Inputs";
import { MonthHeader } from "../../../Utils/TableComponents";
import { getNextMonth } from "../../../Utils/Utils";

export default function LatexProcesoMonth(props) {
    const { month, monthIndex } = props;

    const { setValue, control } = useFormContext();

    const isFirstTable = month === 'enero';
    const LatexProcesoDataCV = useWatch({
        control,
        name: 'ComparativoCostoVentas.LatexProcesoData',
    });
    const BaseSolidosDataCV = 'ComparativoCostoVentas.SolidosData';
    const BaseSKIMDataCV = 'ComparativoCostoVentas.SKIMData';

    // Entradas MP a producción
    const entradasMPAProduccionKgSecos = LatexProcesoDataCV.SKIM[month].KgSecos;
    const entradasMPAProduccionCostoKgSec = LatexProcesoDataCV.SKIM[month].CostoKgSec;
    const entradasMPAProduccionValorTotal = useMemo(() =>
        entradasMPAProduccionKgSecos * entradasMPAProduccionCostoKgSec,
        [entradasMPAProduccionKgSecos, entradasMPAProduccionCostoKgSec]
    );

    // (-) SKIM para sólidos
    const skimParaSolidosKgSecos = useWatch({
        control,
        name: `${BaseSKIMDataCV}.SKIMParaSolidos.${month}.KgSecos`,
    });
    const skimParaSolidosCostoKgSec = LatexProcesoDataCV.SKIM[month].CostoKgSec;
    const skimParaSolidosValorTotal = useMemo(() => skimParaSolidosKgSecos * skimParaSolidosCostoKgSec, [skimParaSolidosKgSecos, skimParaSolidosCostoKgSec]);

    // (+) Gtos. Fabricación del mes
    const gtosFabricacionMesCostoKgSec = useWatch({
        control,
        name: `${BaseSolidosDataCV}.GtosFabricacionDelMes.${month}.CostoKgSec`,
    });
    const gtosFabricacionMesValorTotal = useMemo(() =>
        (entradasMPAProduccionKgSecos || 0) * (gtosFabricacionMesCostoKgSec || 0),
        [entradasMPAProduccionKgSecos, gtosFabricacionMesCostoKgSec]
    );

    // (=) Producción del mes (resta SKIM para sólidos, suma Gtos. Fabricación)
    const produccionDelMesKgSecos = useMemo(() =>
        entradasMPAProduccionKgSecos + (skimParaSolidosKgSecos || 0),
        [entradasMPAProduccionKgSecos, skimParaSolidosKgSecos]
    );
    const produccionDelMesValorTotal = useMemo(() =>
        entradasMPAProduccionValorTotal + (skimParaSolidosValorTotal || 0) + gtosFabricacionMesValorTotal,
        [entradasMPAProduccionValorTotal, skimParaSolidosValorTotal, gtosFabricacionMesValorTotal]
    );
    const produccionDelMesCostoKgSec = useMemo(() =>
        entradasMPAProduccionCostoKgSec + gtosFabricacionMesCostoKgSec,
        [entradasMPAProduccionCostoKgSec, gtosFabricacionMesCostoKgSec]
    );

    // (+) Prod. Term. Mes anterior
    const prodTermMesAnteriorKgSecos = useWatch({
        control,
        name: `${BaseSKIMDataCV}.ProdTermMesAnterior.${month}.KgSecos`,
    });
    const prodTermMesAnteriorCostoKgSec = useWatch({
        control,
        name: `${BaseSKIMDataCV}.ProdTermMesAnterior.${month}.CostoKgSec`,
    })
    const prodTermMesAnteriorValorTotal = useMemo(() =>
        prodTermMesAnteriorKgSecos * prodTermMesAnteriorCostoKgSec,
        [prodTermMesAnteriorKgSecos, prodTermMesAnteriorCostoKgSec]
    );

    // (-) Skim reclasificado a latex por maquila
    const skimReclasificadoKgSecos = useWatch({
        control,
        name: `${BaseSKIMDataCV}.SKIMReclasificadoALatexPorMaquila.${month}.KgSecos`,
    });
    const skimReclasificadoCostoKgSec = useWatch({
        control,
        name: `${BaseSKIMDataCV}.SKIMReclasificadoALatexPorMaquila.${month}.CostoKgSec`,
    });
    const skimReclasificadoValorTotal = useMemo(() =>
        skimReclasificadoKgSecos * skimReclasificadoCostoKgSec,
        [skimReclasificadoKgSecos, skimReclasificadoCostoKgSec]
    );

    // (=) Prod. Term. Disponible (incluye la reclasificación)
    const prodTermDisponibleKgSecos = useMemo(() =>
        produccionDelMesKgSecos + prodTermMesAnteriorKgSecos + (skimReclasificadoKgSecos || 0), // skimReclasificadoKgSecos ya es negativo
        [produccionDelMesKgSecos, prodTermMesAnteriorKgSecos, skimReclasificadoKgSecos]
    );
    const prodTermDisponibleValorTotal = useMemo(() =>
        produccionDelMesValorTotal + prodTermMesAnteriorValorTotal + (skimReclasificadoValorTotal || 0), // skimReclasificadoValorTotal ya es negativo
        [produccionDelMesValorTotal, prodTermMesAnteriorValorTotal, skimReclasificadoValorTotal]
    );
    const prodTermDisponibleCostoKgSec = useMemo(() =>
        prodTermDisponibleKgSecos > 0 ? prodTermDisponibleValorTotal / prodTermDisponibleKgSecos : 0,
        [prodTermDisponibleValorTotal, prodTermDisponibleKgSecos]
    );

    // (-) Costo Vta. Del Mes
    const costoVtaDelMesKgSecos = useWatch({
        control,
        name: `${BaseSKIMDataCV}.CostoVtaDelMes.${month}.KgSecos`,
    })

    const costoVtaDelMesKgSecosAbs = Math.abs(costoVtaDelMesKgSecos || 0);
    const costoVtaDelMesCostoKgSec = useMemo(() => {
        if (costoVtaDelMesKgSecosAbs > 0) return prodTermDisponibleCostoKgSec;
        return 0
    }, [costoVtaDelMesKgSecosAbs, prodTermDisponibleCostoKgSec]);
    const costoVtaDelMesValorTotal = useMemo(() =>
        costoVtaDelMesKgSecosAbs * costoVtaDelMesCostoKgSec,
        [costoVtaDelMesKgSecosAbs, costoVtaDelMesCostoKgSec]
    );

    // (=) Prod. Termin. Final = disponible - costo de venta (convención positiva)
    const prodTerminFinalKgSecos = useMemo(() =>
        prodTermDisponibleKgSecos - costoVtaDelMesKgSecosAbs,
        [prodTermDisponibleKgSecos, costoVtaDelMesKgSecosAbs]
    );

    const prodTerminFinalCostoKgSec = useMemo(() =>
        prodTermDisponibleCostoKgSec,
        [prodTermDisponibleCostoKgSec]
    );
    const prodTerminFinalValorTotal = useMemo(() =>
        prodTerminFinalKgSecos * prodTerminFinalCostoKgSec,
        [prodTerminFinalKgSecos, prodTerminFinalCostoKgSec]
    );

    // CASOS ESPECIALES
    // Prod. Term. Mes anterior para enero y meses posteriores
    let prodTermMesAnteriorRow = null;
    if (!isFirstTable) {
        prodTermMesAnteriorRow = (
            <tr>
                <td><KgDisplay value={prodTermMesAnteriorKgSecos} /></td>
                <td><CurrencyDisplay value={prodTermMesAnteriorCostoKgSec} decimalScale={5} /></td>
                <td><CurrencyDisplay value={prodTermMesAnteriorValorTotal} /></td>
            </tr>
        );
    } else {
        prodTermMesAnteriorRow = (
            <tr>
                <td>
                    <KgInput
                        name={`${BaseSKIMDataCV}.ProdTermMesAnterior.${month}.KgSecos`} />
                </td>
                <td>
                    <CurrencyInput
                        name={`${BaseSKIMDataCV}.ProdTermMesAnterior.${month}.CostoKgSec`}
                        decimalScale={5} />
                </td>
                <td><CurrencyDisplay value={prodTermMesAnteriorValorTotal} /></td>
            </tr>
        );
    }

    // Actualizar valores de Prod. Term. Mes anterior
    useEffect(() => {
        const nextMonth = getNextMonth(monthIndex);
        if (nextMonth === 'enero') return;
        if (prodTerminFinalKgSecos === 0) return; 

        setValue(`${BaseSKIMDataCV}.ProdTermMesAnterior.${nextMonth}.KgSecos`, prodTerminFinalKgSecos);
        setValue(`${BaseSKIMDataCV}.ProdTermMesAnterior.${nextMonth}.CostoKgSec`, prodTerminFinalCostoKgSec);
    }, [
        prodTerminFinalKgSecos, prodTerminFinalCostoKgSec, prodTerminFinalValorTotal,
        month, setValue
    ]);

    // Para guardar Costo Vta Del Mes en CostoKgSec
    useEffect(() => {
        setValue(`${BaseSKIMDataCV}.CostoVtaDelMes.${month}.CostoKgSec`, costoVtaDelMesCostoKgSec);
    }, [costoVtaDelMesCostoKgSec, month, setValue]);

    // Para guardar Prod. Termin. Final
    useEffect(() => {
        setValue(`${BaseSKIMDataCV}.ProdTerminFinal.${month}.KgSecos`, prodTerminFinalKgSecos);
        setValue(`${BaseSKIMDataCV}.ProdTerminFinal.${month}.CostoKgSec`, prodTerminFinalCostoKgSec);
    }, [prodTerminFinalKgSecos, prodTerminFinalCostoKgSec, month, setValue]);

    return (
        <table key={monthIndex}>
            <MonthHeader month={month} monthIndex={monthIndex} />
            <tbody>
                {/* Entradas MP a producción */}
                <tr>
                    <td><KgDisplay value={entradasMPAProduccionKgSecos} /></td>
                    <td><CurrencyDisplay value={entradasMPAProduccionCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={entradasMPAProduccionValorTotal} /></td>
                </tr>
                {/* (-) SKIM para sólidos */}
                <tr>
                    <td>
                        <NegativeKgInput
                            name={`${BaseSKIMDataCV}.SKIMParaSolidos.${month}.KgSecos`} />
                    </td>
                    <td>
                        <CurrencyDisplay value={skimParaSolidosCostoKgSec} decimalScale={5} />
                    </td>
                    <td><CurrencyDisplay value={skimParaSolidosValorTotal} /></td>
                </tr>
                {/* (+) Gtos. Fabricación del mes */}
                <tr>
                    <td>
                        
                    </td>
                    <td>
                        <CurrencyDisplay
                            value={gtosFabricacionMesCostoKgSec}
                            decimalScale={5} />
                    </td>
                    <td><CurrencyDisplay value={gtosFabricacionMesValorTotal} /></td>
                </tr>
                {/* (=) Producción del mes */}
                <tr className="celda-totales">
                    <td><KgDisplay value={produccionDelMesKgSecos} /></td>
                    <td><CurrencyDisplay value={produccionDelMesCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={produccionDelMesValorTotal} /></td>
                </tr>
                {/* (+) Prod. Term. Mes anterior */}
                {prodTermMesAnteriorRow}
                {/* (-) Skim reclasificado a latex por maquila */}
                <tr>
                    <td>
                        <NegativeKgInput
                            name={`${BaseSKIMDataCV}.SKIMReclasificadoALatexPorMaquila.${month}.KgSecos`} />
                    </td>
                    <td>
                        <CurrencyInput
                            name={`${BaseSKIMDataCV}.SKIMReclasificadoALatexPorMaquila.${month}.CostoKgSec`}
                            decimalScale={5} />
                    </td>
                    <td><CurrencyDisplay value={skimReclasificadoValorTotal} /></td>
                </tr>
                {/* (=) Prod. Term. Disponible */}
                <tr className="celda-totales">
                    <td><KgDisplay value={prodTermDisponibleKgSecos} /></td>
                    <td><CurrencyDisplay value={prodTermDisponibleCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={prodTermDisponibleValorTotal} /></td>
                </tr>
                {/* (-) Costo Vta. Del Mes */}
                <tr>
                    <td>
                        <KgInput
                            name={`${BaseSKIMDataCV}.CostoVtaDelMes.${month}.KgSecos`} />
                    </td>
                    <td><CurrencyDisplay value={costoVtaDelMesCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={costoVtaDelMesValorTotal} /></td>
                </tr>
                {/* (=) Prod. Termin. Final */}
                <tr className="celda-totales">
                    <td><KgDisplay value={prodTerminFinalKgSecos} /></td>
                    <td><CurrencyDisplay value={prodTerminFinalCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={prodTerminFinalValorTotal} /></td>
                </tr>
            </tbody>
        </table>
    );
}
